import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import api from "../../../config/URL";
import "../../../styles/admin.css";
import { FaTrash } from "react-icons/fa";
import { FiAlertTriangle } from "react-icons/fi";

function CampaignAdd() {
  const [loadIndicator, setLoadIndicator] = useState(false);
  const navigate = useNavigate();

  const MAX_FILE_SIZE = 2 * 1024 * 1024;
  const SUPPORTED_FORMATS = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/svg+xml",
    "image/webp",
  ];

  const generateSlug = (title) => title.toLowerCase()
        .replace(/[\s/\\]+/g, "-") 
        .replace(/(?<=\d)\.(?=\d)|\.(?=[a-z])/g, "[dot]")
        .replace(/[^a-z0-9-\[\]dot]/g, "") 
        .replace(/\[dot\]/g, ".") 
        .replace(/-+/g, "-") 
        .replace(/^-+|-+$/g, ""); 

  const imageValidation = Yup.mixed()
    .nullable()
    .test("fileFormat", "Unsupported format", (value) => {
      return !value || (value && SUPPORTED_FORMATS.includes(value.type));
    })
    .test("fileSize", "File size is too large. Max 2MB.", (value) => {
      return !value || (value && value.size <= MAX_FILE_SIZE);
    });

  const validationSchema = Yup.object({
    title: Yup.string()
      .required("*Please fill the title field")
      .max(255, "*Title must not exceed 255 characters"),
    image: imageValidation.required("*Image is required"),
    description: Yup.string(),
    web_url: Yup.string().max(255, "*Web URL must not exceed 255 characters"),
    custom_select_label_required: Yup.boolean(), // this is the flag

    custom_select_label: Yup.string().when("custom_select_label_required", {
      is: true,
      then: (schema) => schema.required("*Please this field is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    custom_select_options: Yup.array().when("custom_select_label_required", {
      is: true,
      then: (schema) =>
        schema
          .of(Yup.string().required("*Option cannot be empty"))
          .min(1, "*Please add at least one option")
          .required("*Please provide at least one custom option"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      image: null,
      description: "",
      web_url: "",
      show_name: false,
      show_email: false,
      show_phone: false,
      show_address: false,
      custom_select_required: false,
      custom_select_label: "",
      custom_select_options: ["", ""],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const formData = new FormData();

      formData.append("title", values.title);
      formData.append("slug", generateSlug(values.title));
      formData.append("description", values.description || "");
      formData.append("web_url", values.web_url || "");
      formData.append("show_name", values.show_name ? 1 : 0);
      formData.append("show_email", values.show_email ? 1 : 0);
      formData.append("show_phone", values.show_phone ? 1 : 0);
      formData.append("show_address", values.show_address ? 1 : 0);
      formData.append(
        "custom_select_required",
        values.custom_select_required ? 1 : 0
      );
      formData.append("custom_select_label", values.custom_select_label || "");

      // Filter out empty or null options before appending
      const filteredOptions = values.custom_select_options.filter(
        (option) => option !== null && option.trim() !== ""
      );

      filteredOptions.forEach((option, index) => {
        formData.append(`custom_select_options[${index}]`, option);
      });

      if (values.image) {
        formData.append("image", values.image);
      }

      setLoadIndicator(true);
      try {
        const response = await api.post(`admin/campaign`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        if (response.status === 200) {
          toast.success(response.data.message);
          navigate("/campaign");
        }
      } catch (error) {
        if (error.response) {
          if (error.response.status === 422) {
            const errors = error.response.data.errors;
            if (errors) {
              Object.keys(errors).forEach((key) => {
                errors[key].forEach((errorMsg) => {
                  toast(errorMsg, {
                    icon: <FiAlertTriangle className="text-warning" />,
                  });
                });
              });
            }
          } else {
            toast.error(
              error.response.data.message || "An unexpected error occurred."
            );
          }
        } else {
          toast.error("An unexpected error occurred.");
        }
      }
      setLoadIndicator(false);
    },
  });

  const handleFileChange = (event) => {
    const file = event?.target?.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error("File size is too large. Max 2MB.");
        event.target.value = null;
        formik.setFieldValue("image", null);
        return;
      }

      formik.setFieldValue("image", file);
    }
  };

  return (
    <section className="px-4">
      <form onSubmit={formik.handleSubmit}>
        <div className="card shadow border-0 mb-3">
          <div className="row p-3">
            <div className="d-flex justify-content-between align-items-center">
              <h1 className="h4 ls-tight">Add Campaign</h1>
              <Link to="/campaign">
                <button type="button" className="btn btn-light btn-sm">
                  <span>Back</span>
                </button>
              </Link>
            </div>
          </div>
        </div>

        <div
          className="container card shadow border-0"
          style={{ minHeight: "60vh" }}
        >
          <div className="row mt-3 mb-5">
            {/* Title */}
            <div className="col-md-6 col-12 file-input mb-3">
              <label className="form-label">Title</label>
              <input
                type="text"
                placeholder="Name"
                className={`form-control ${
                  formik.touched.title && formik.errors.title
                    ? "is-invalid"
                    : ""
                }`}
                {...formik.getFieldProps("title")}
              />
              {formik.touched.title && formik.errors.title && (
                <div className="invalid-feedback">{formik.errors.title}</div>
              )}
            </div>

            {/* Web URL */}
            <div className="col-md-6 col-12 mb-3">
              <label className="form-label">Web URL</label>
              <input
                type="text"
                className="form-control"
                {...formik.getFieldProps("web_url")}
              />
            </div>

            {/* Image */}
            <div className="col-md-6 col-12 mb-3">
              <label className="form-label">
                Image <span className="text-danger">*</span>
              </label>
              <input
                type="file"
                accept=".png,.jpeg,.jpg,.svg,.webp"
                className={`form-control ${
                  formik.touched.image && formik.errors.image
                    ? "is-invalid"
                    : ""
                }`}
                name="image"
                onChange={handleFileChange}
                onBlur={formik.handleBlur}
              />
              <p style={{ fontSize: "13px" }}>
                Note: Maximum file size is 2MB. Allowed: .png, .jpg, .jpeg,
                .svg, .webp.
              </p>
              {formik.touched.image && formik.errors.image && (
                <div className="invalid-feedback">{formik.errors.image}</div>
              )}
            </div>

            {/* Description */}
            <div className="col-md-6 col-12 mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows="5"
                {...formik.getFieldProps("description")}
              />
            </div>

            {/* Display Fields including Custom Field */}
            <div className="col-md-6 col-12 mb-3 campaignCheck">
              <label className="form-label">Display Fields</label>
              <div className="d-flex flex-wrap gap-4">
                {[
                  { key: "show_name", label: "Name" },
                  { key: "show_email", label: "Email" },
                  { key: "show_phone", label: "Phone" },
                  { key: "show_address", label: "Address" },
                  { key: "custom_select_required", label: "Custom Field" },
                ].map(({ key, label }) => (
                  <div
                    className="form-check d-flex align-items-center gap-3"
                    key={key}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={key}
                      checked={formik.values[key]}
                      onChange={(e) =>
                        formik.setFieldValue(key, e.target.checked)
                      }
                    />
                    <label className="form-check-label mt-2" htmlFor={key}>
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Select Label */}

            {formik.values.custom_select_required && (
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">Custom Label</label>
                <input
                  type="text"
                  className={`form-control ${
                    formik.touched.custom_select_label &&
                    formik.errors.custom_select_label
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("custom_select_label")}
                />
                {formik.touched.custom_select_label &&
                  formik.errors.custom_select_label && (
                    <div className="invalid-feedback">
                      {formik.errors.custom_select_label}
                    </div>
                  )}
              </div>
            )}

            {/* Custom Select Options */}
            {formik.values.custom_select_required && (
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">Custom Options</label>
                {formik.values.custom_select_options.map((option, index) => (
                  <div
                    key={index}
                    className="input-group mb-2"
                    style={{ maxWidth: "400px" }}
                  >
                    <input
                      type="text"
                      className={`form-control ${
                        formik.touched.custom_select_options?.[index] &&
                        formik.errors.custom_select_options?.[index]
                          ? "is-invalid"
                          : ""
                      }`}
                      value={option}
                      onChange={(e) => {
                        const updated = [
                          ...formik.values.custom_select_options,
                        ];
                        updated[index] = e.target.value;
                        formik.setFieldValue("custom_select_options", updated);
                      }}
                    />
                    {formik.values.custom_select_options.length > 2 &&
                      index >= 2 && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => {
                            const updated =
                              formik.values.custom_select_options.filter(
                                (_, i) => i !== index
                              );
                            formik.setFieldValue(
                              "custom_select_options",
                              updated
                            );
                          }}
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                          }}
                        >
                          <FaTrash />
                        </button>
                      )}
                    {formik.touched.custom_select_options?.[index] &&
                      formik.errors.custom_select_options?.[index] && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.custom_select_options[index]}
                        </div>
                      )}
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-option btn-sm mt-2"
                  onClick={() =>
                    formik.setFieldValue("custom_select_options", [
                      ...formik.values.custom_select_options,
                      "",
                    ])
                  }
                >
                  Add Option
                </button>
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="hstack p-2 mt-5">
            <button
              type="submit"
              className="btn btn-sm btn-button"
              disabled={loadIndicator}
            >
              {loadIndicator && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
              )}
              Add
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}

export default CampaignAdd;
